import React, { useMemo, useState } from 'react';

import { BaseReact } from '../../../base.model';
import { ProfileQL } from '../../../models/Profile'
import SectionTitleBar from '../../components/titlebar';
import { useQuery } from '@apollo/react-hooks';
import { graphqlSchema } from '../../../services/graphql.schema';

import SetupMenu from '../Setup.menu';
import AccountAvatarHolder from '../../components/accountAvatarHolder';
import AccountAvatarHolderMobile from '../../components/accountAvatarHolder.mobile';
import { StripePricingPlan } from '../../../models/Stripe';
import SetupPagosHistory from './Setup.Pagos.History';
import AccountInfo from './AccountInfo/AccountInfo';
import AccountPayment from './PaymentDialogs/Account.payment';
import AccountPlans from './AccountPlans/AccountPlans';
import GraphQlClient from '../../../services/graphql';
import './Setup.Pagos.scss';
import { onlyPaymentPlansFeatureFlag } from './helpers/onlyPlansFeatureFlag';
import AccountPlansModal from './AccountPlans/AccountPlansModal';
import AccountPaymentChoice from './PaymentDialogs/Account.payment.choice';

interface SetupProps extends BaseReact {
    currentUser: ProfileQL;
}

function SetupPagosFiscalPop({ currentUser, history }: SetupProps) {
    // Card States
    const [addPayment, setAddPayment] = useState(false);
    const [showPaymentChoice, setShowPaymentChoice] = useState(false);
    const [showPlansModal, setShowPlansModal] = useState(false);
    // Prices
    const [pricingPlans, setPricingPlans] = useState<StripePricingPlan[]>([]);

    // Transitioning out of "Recarga" in favour of Payment plans only
    const ONLY_PAYMENT_PLANS_FF = useMemo(() => {
        return onlyPaymentPlansFeatureFlag(currentUser);
    }, [currentUser]);

    useQuery(graphqlSchema.STRIPE.PRICES.getPricingPlans, {
        onCompleted: ({ getPricingPlans }: { getPricingPlans: StripePricingPlan[] }) => {
            console.log('Pricing plans gotten: ', getPricingPlans);
            const hasLinker = !!currentUser?.professionalProfile?.linkerId;
            const hasSubscription = !!currentUser?.billing?.hasStripeSubscription;
            const notMax50Plan = !hasSubscription ? false : !!currentUser?.billing?.stripeSubscriptionCfdiUsage && currentUser.billing.stripeSubscriptionCfdiUsage.maxUsage !== 50;
            const shouldHide50Plan = hasLinker && notMax50Plan; // Hide 50 CFDI plan for Linked accounts or those who already have a subscription that isn't the 50 CFDI plan, since it wouldn't make sense to show it as an option.

            if (shouldHide50Plan) {
                // Exclude the 50 CFDI plan for Linked accounts IF they don't already have it
                setPricingPlans(getPricingPlans.filter(p => p.maxUsage !== 50));
            } else {
                setPricingPlans(getPricingPlans);
            }
        }
    });

    const cheapestPlanCfdiCostPesos = useMemo(() => {
        if (!pricingPlans?.length) return null;
        const minCents = pricingPlans.filter(p => !p.isMeteredBilling).reduce<number | null>((acc, plan) => {
            if (typeof plan.cfdiMinAmountCents !== 'number') return acc;
            if (acc === null) return plan.cfdiMinAmountCents;
            return Math.min(acc, plan.cfdiMinAmountCents);
        }, null);
        return minCents === null ? null : minCents / 100;
    }, [pricingPlans]);

    const isClusterAccount = useMemo(() => {
        return !!(currentUser?.billing?.isClusterType && currentUser?.billing?.clusterName);
    }, [currentUser]);

    const closePaymentModal = (paymentResponse: { success: boolean } = null) => {
        setAddPayment(false);
        if (!paymentResponse) {
            return;
        }
        if (paymentResponse.success) {
            setTimeout(() => {
                // Wait for Webhook to process
                GraphQlClient.recallCurrentUser();
            }, 3000);
        }
        console.log('Payment response onClose: ', paymentResponse);
    }


    const openAddBalanceFlow = () => {
        if (isClusterAccount) {
            return;
        }
        if (ONLY_PAYMENT_PLANS_FF) {
            setShowPlansModal(true);
        } else {
            setShowPaymentChoice(true);
        }
    }

    const handleChoosePlan = () => {
        setShowPaymentChoice(false);
        setShowPlansModal(true);
    }

    const handleChooseRecarga = () => {
        setShowPaymentChoice(false);
        setAddPayment(true);
    }

    return (
        <div id="SetupPagosFiscalPop">
            <SectionTitleBar currentUser={currentUser} title="Planes & Pagos" />
            <div className="setupContent">
                <SetupMenu />
                <div className='planSectionSplit'>
                    <div className="plansAndBalance">
                        <div className="card balanceInfo">
                            <AccountAvatarHolder currentUser={currentUser} hideName />
                            <AccountAvatarHolderMobile currentUser={currentUser} />
                            <AccountInfo
                                pricingPlans={pricingPlans}
                                isClusterAccount={isClusterAccount}
                                billingAccount={currentUser.billing}
                                onAddBalanceClick={openAddBalanceFlow}
                                onlyPaymentPlans={ONLY_PAYMENT_PLANS_FF}
                            />
                        </div>
                        <AccountPlans
                            pricingPlans={pricingPlans}
                            isClusterAccount={isClusterAccount}
                            billingAccount={currentUser.billing}
                        />
                    </div>
                    <div className="paymentsAndHistory">
                        <SetupPagosHistory
                            currentUser={currentUser}
                        />
                    </div>
                </div>
            </div>
            <AccountPayment isOpen={addPayment} onRequestClose={closePaymentModal} />
            <AccountPaymentChoice
                isOpen={showPaymentChoice}
                onRequestClose={() => setShowPaymentChoice(false)}
                onChoosePlan={handleChoosePlan}
                onChooseRecarga={handleChooseRecarga}
                cheapestPlanCfdiCostPesos={cheapestPlanCfdiCostPesos}
            />
            <AccountPlansModal
                isOpen={showPlansModal}
                onRequestClose={() => setShowPlansModal(false)}
                pricingPlans={pricingPlans}
                isClusterAccount={isClusterAccount}
                billingAccount={currentUser.billing}
            />
        </div >
    )
}

export default SetupPagosFiscalPop;
